import { trackClickEvent } from './analytics'

/**
 * Tracks language change events for analytics
 * @param previousLanguage The previous language setting
 * @param newLanguage The new language setting
 * @param userId Optional user ID (may be missing during registration)
 * @param groupId Optional group ID from member.groupId (may be missing during registration)
 * @param isDuringRegistration Whether this language change occurred during registration
 */
export const trackLanguageChange = (
  previousLanguage: string | null,
  newLanguage: string,
  userId?: string,
  groupId?: string,
  isDuringRegistration = false,
): void => {
  try {
    const eventProperties: Record<string, any> = {
      previousLanguage,
      newLanguage,
      timestamp: new Date().toISOString(),
    }

    // Add user identifiers if available
    if (userId) {
      eventProperties.memberId = userId
    }

    if (groupId) {
      eventProperties.accountID = groupId // Uses uppercase ID to match other analytics events
    }

    // Use different event names based on context
    const eventName = isDuringRegistration ? 'displayLanguageChangeDuringRegistration' : 'displayLanguageChange'

    trackClickEvent(eventName, eventProperties)
  } catch (error) {
    console.error('Failed to track language change event', error)
  }
}

/**
 * Tracks when Spanish language is automatically detected and set from browser language during i18n initialization
 * @param browserLanguage The raw browser language code (e.g., 'es-ES', 'es-MX')
 * @param normalizedLanguage The normalized language code that was set ('es')
 */
export const trackSpanishAutoDetection = (browserLanguage: string, normalizedLanguage: string): void => {
  try {
    const eventProperties: Record<string, any> = {
      browserLanguage,
      normalizedLanguage,
      detectionMethod: 'browser',
      timestamp: new Date().toISOString(),
    }

    trackClickEvent('spanishLanguageAutoDetected', eventProperties)
  } catch (error) {
    console.error('Failed to track Spanish auto-detection event', error)
  }
}

/**
 * Tracks the user's browser language during i18n initialization
 * This helps track what languages our users prefer, even if we don't support them yet
 * @param browserLanguage The raw browser language code (e.g., 'es-ES', 'en-US', 'zh-CN')
 * @param normalizedLanguage The normalized language code that was set (e.g., 'es', 'en', 'zh')
 * @param isFirstDetection Whether this was the first detection (no saved preference)
 */
export const trackBrowserLanguage = (
  browserLanguage: string,
  normalizedLanguage: string,
  isFirstDetection: boolean,
): void => {
  try {
    const eventProperties: Record<string, any> = {
      browserLanguage,
      normalizedLanguage,
      isFirstDetection,
      timestamp: new Date().toISOString(),
    }

    trackClickEvent('browserLanguageDetected', eventProperties)
  } catch (error) {
    console.error('Failed to track browser language detection', error)
  }
}
